import BigNumber from "bignumber.js";
import dayjs from "dayjs";

export async function exponentialBackoff(
  promiseFunction,
  failureFunction,
  err,
  totalAllowedBackoffTries = 5,
  backoffAttempt = 1
) {
  const backoffSecondsToWait = 2 + Math.pow(backoffAttempt, 2);

  if (backoffAttempt > totalAllowedBackoffTries) throw err;

  try {
    const result = await promiseFunction();
    return result;
  } catch (err) {
    if (failureFunction) failureFunction(err, backoffAttempt);
    await sleep(backoffSecondsToWait * 1000);
    return await exponentialBackoff(
      promiseFunction,
      failureFunction,
      err,
      totalAllowedBackoffTries,
      backoffAttempt + 1
    );
  }
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounce(func, waitMs = 500, immediate = false) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    const later = function () {
      // timeout = null
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, waitMs);
    if (callNow) func.apply(context, args);
  };
}

export function formatDynamicDecimals(number, decimalPadding = 1) {
  const bnNum = new BigNumber(number);
  if (bnNum.isNaN() || bnNum.lt("0.000000000000000001")) {
    return 0;
  }

  let multiplier = 0.01;
  let decimals = 0;

  const t = true;
  while (t) {
    if (
      bnNum
        .times(multiplier)
        .gte(
          new BigNumber(10).pow(
            new BigNumber(decimalPadding).minus(1)
          )
        )
    ) {
      const returnValue = new BigNumber(bnNum).toFixed(decimals);
      if (new BigNumber(bnNum.toFixed(0)).eq(returnValue)) {
        return bnNum.toFormat(0);
      }

      return bnNum.toFormat(decimals);
    } else {
      decimals++;
      multiplier *= 10;
    }
  }
}

export function processMulticallValue(val) {
  if (val instanceof Array) {
    return val.map((v) => processMulticallValue(v));
  }
  if (typeof val === "number") {
    return val;
  }
  if (!val && typeof val !== "boolean") {
    return null;
  }
  if (val.toString() === "[object Object]") {
    return new BigNumber(val.hex.toLowerCase()).toFixed();
  }
  return val;
}

export function formatNumberCompact(number) {
  const formatter = Intl.NumberFormat("en", { notation: "compact" });
  return formatter.format(number);
}

export function formatNumber(number, decimal = 2) {
  return new BigNumber(
    number.toString().replace(/[$,]/g, "")
  ).toFormat(decimal);
}

export function getShortAddress(address) {
  address = address || "";
  const f3 = address.slice(0, 6);
  const l3 = address.slice(-4);
  return `${f3}...${l3}`;
}

export function formatUnixDate(
  date,
  format = "MMMM D, YYYY HH:mm a"
) {
  return dayjs.unix(date).format(format);
}

export async function onCopy(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch ($e) {}
}

export async function sleep(milliseconds) {
  return await new Promise((resolve) =>
    setTimeout(resolve, milliseconds)
  );
}
